import { copy } from './copy';
import type { ConfigurationRow, ShelfParamKind } from './types';

/**
 * The source vocabulary, and what each member resolves against.
 *
 * **The set itself is never compiled in.** `SHELF_SOURCES` is an admin-tunable
 * configured value whose key is seeded from code and whose members are edited
 * through the runtime-configuration surface — a ninth source is registered
 * there, not shipped in a release. So the members this surface offers come from
 * the fetched configuration row, and everything below is a *dictionary over*
 * that value rather than a copy of it:
 *
 * - `sourceLabel` is a display map. A member with no entry renders as **its own
 *   key**, exactly as an unmapped report tile does — a source the console has
 *   no wording for is still a source, and dropping it would hide a configured
 *   value from the operator who configured it.
 * - `paramKindFor` is a **code-owned** contract quoted from the owning
 *   service's own schema, not a setting: `content__shelves` carries a `CHECK`
 *   on the three parameter kinds, and names the five members that must carry
 *   `NULL`. A configured member with no mapping is therefore treated as
 *   parameterless, and the server's `422` remains the authority on a write.
 */

/** Whichever of the configured sources scope to something. */
const PARAM_KIND_BY_SOURCE: Readonly<Record<string, ShelfParamKind>> = {
  category: 'CATEGORY',
  tag: 'TAG',
  editorial_hand_pick: 'CONTENT_ITEM_SET',
};

/**
 * The shape a newly created shelf is authored with.
 *
 * **This is not a picker, and the catalog forbids one**: the layout «belongs to
 * the shelf's declared identity» and «no operator surface offers a layout
 * picker». `POST /admin/shelves` nonetheless requires the field — it is
 * validated against `SHELF_LAYOUTS`, an admin-tunable setting whose members are
 * `hero_slider,grid,landscape_row,portrait_row` — so the console has to author
 * one without asking, and the pairing below is read off the shelf list the
 * platform ships rather than invented here: a genre or tag rail is a portrait
 * row, a recency or popularity rail is a landscape row, an editorial hand-pick
 * is a grid.
 *
 * **`hero_slider` is deliberately unreachable.** It is the home page's
 * largest-contentful element and the ordered shelf list places it first for
 * that reason; a second one created from a dialog would move the metric the
 * billboard is budgeted for. A slider is authored with the shelf list.
 *
 * An unmapped source — a ninth registered in the setting after this shipped —
 * gets the landscape row, and the server's own
 * `CONTENT_SHELF_LAYOUT_NOT_CONFIGURED` stays the authority.
 */
const LAYOUT_BY_SOURCE: Readonly<Record<string, string>> = {
  category: 'portrait_row',
  tag: 'portrait_row',
  saved_list: 'portrait_row',
  editorial_hand_pick: 'grid',
  recency: 'landscape_row',
  popularity: 'landscape_row',
  continue_watching: 'landscape_row',
};

const DEFAULT_LAYOUT = 'landscape_row';

/**
 * The one configured source that belongs to a title page rather than a home.
 * It is in the vocabulary, so it is offered — offering it without the hint
 * beside it would invite a shelf that renders nothing.
 */
export const OFF_HOME_SOURCE = 'related_to_current_item';

/** The two whose rows resolve against the signed-in viewer. */
const VIEWER_SCOPED_SOURCES = new Set(['continue_watching', 'saved_list']);

/**
 * A dictionary read on a key the dictionary may not carry.
 *
 * The copy maps are literal objects so their own fallback entries keep a known
 * type; every dynamic read goes through here, which is where the «may be
 * absent» half is stated once rather than at each call site.
 */
export function lookup(table: Readonly<Record<string, string>>, key: string): string | undefined {
  return (table as Readonly<Record<string, string | undefined>>)[key];
}

export function paramKindFor(source: string): ShelfParamKind | null {
  return PARAM_KIND_BY_SOURCE[source] ?? null;
}

export function layoutFor(source: string): string {
  return lookup(LAYOUT_BY_SOURCE, source) ?? DEFAULT_LAYOUT;
}

/**
 * The parameter pair, as the two columns the service writes.
 *
 * The kind decides both halves: a parameterless source must send `null` for
 * each — a value on one is `422 CONTENT_SHELF_SOURCE_PARAMETER_INVALID` — and a
 * scoped source sends its ordered members joined on the comma the column stores
 * them in. A hand-pick with nothing picked yet sends the **empty string** and
 * not `null`: the three scoped sources are refused a null value, and a shelf
 * nobody has picked for is still a hand-picked shelf.
 */
export function sourceParamFields(
  source: string,
  values: readonly string[],
): {
  sourceParamKind: ShelfParamKind | null;
  sourceParamValue: string | null;
} {
  const kind = paramKindFor(source);
  return kind
    ? { sourceParamKind: kind, sourceParamValue: values.join(',') }
    : { sourceParamKind: null, sourceParamValue: null };
}

export function isViewerScoped(source: string): boolean {
  return VIEWER_SCOPED_SOURCES.has(source);
}

export function sourceLabel(source: string): string {
  return lookup(copy.sources, source) ?? source;
}

/**
 * The second line of the source cell — **always present**, even where there is
 * no parameter. A silent cell reads as «forgotten» rather than as «needs
 * nothing», and the sentence is per source because «يحلّ بنفسه» is true of
 * recency and misleading for a source that resolves against the viewer.
 */
export function selfResolvingNote(source: string): string {
  return lookup(copy.selfResolving, source) ?? copy.selfResolving.default;
}

/**
 * The csv settings this surface reads. Empty rather than a guess when the
 * configuration read has not landed: an invented default here would be the one
 * value the product never chose.
 */
export function readCsvSetting(rows: readonly ConfigurationRow[] | undefined, key: string): string[] {
  const row = rows?.find((candidate) => candidate.key === key);
  if (!row) return [];

  return row.value
    .split(',')
    .map((member) => member.trim())
    .filter(Boolean);
}
